import { Modal, Spin } from 'antd';
import { AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useFetchCarrierRestrictedItems } from '@/hooksApi';
import { CategoryCard } from './CategoryCard';

interface RestrictedItemsModalProps {
  carrierName: string;
  carrierSlug: string;
  open: boolean;
  onClose: () => void;
  mode: 'enable' | 'view';
  onConfirmEnable?: () => void;
}

export function RestrictedItemsModal({
  carrierName,
  carrierSlug,
  open,
  onClose,
  mode,
  onConfirmEnable,
}: RestrictedItemsModalProps) {
  const { t } = useTranslation();
  const { data, isLoading } = useFetchCarrierRestrictedItems(carrierSlug, open);

  const categories = data?.object?.categories ?? [];
  const disclaimer = data?.object?.disclaimer ?? '';
  const supportText = data?.object?.support_text ?? '';

  const handleConfirm = () => {
    onConfirmEnable?.();
    onClose();
  };

  return (
    <Modal
      className='sd-scope sd-font'
      open={open}
      onCancel={onClose}
      footer={null}
      title={null}
      centered
      destroyOnClose
      closable={false}
      width={620}
      styles={{
        content: {
          borderRadius: '14px',
          padding: 0,
          overflow: 'hidden',
          maxHeight: '85vh',
          display: 'flex',
          flexDirection: 'column' as const,
          background: '#fafbfc',
        },
        mask: { backgroundColor: 'rgba(0,0,0,0.45)' },
        body: { padding: 0 },
      }}
    >
      {/* ── Header ── */}
      <div className='px-6 pt-5 pb-4'>
        <div className='flex items-start justify-between'>
          <div className='flex items-center gap-2'>
            <AlertTriangle className='h-5 w-5 text-amber-500 shrink-0' strokeWidth={2} />
            <h3 className='text-base font-bold text-sd-foreground m-0'>
              {String(t('restricted_items_title', { carrier: carrierName }))}
            </h3>
          </div>
          <button
            onClick={onClose}
            className='flex h-6 w-6 items-center justify-center rounded-sm hover:bg-black/5 transition-colors cursor-pointer bg-transparent border-0 text-sd-muted-foreground hover:text-sd-foreground text-base leading-none'
          >
            ×
          </button>
        </div>
        {disclaimer && !isLoading && (
          <p className='text-[13px] text-sd-muted-foreground m-0 mt-2 leading-[1.55]'>{disclaimer}</p>
        )}
      </div>

      {/* ── Scrollable grid ── */}
      <div className='flex-1 overflow-y-auto' style={{ maxHeight: 'calc(85vh - 180px)' }}>
        {isLoading ? (
          <div className='flex items-center justify-center py-20'>
            <Spin size='default' />
            <span className='ms-3 text-sm text-sd-muted-foreground'>
              {String(t('restricted_items_loading'))}
            </span>
          </div>
        ) : (
          <div className='px-5 py-1 pb-4'>
            {categories.length > 0 && (
              <div className='grid grid-cols-2 gap-3'>
                {categories.map((cat) => (
                  <CategoryCard key={cat.id} category={cat} />
                ))}
              </div>
            )}

            {supportText && (
              <p className='text-[11.5px] text-sd-muted-foreground m-0 text-center mt-4'>
                {supportText}
              </p>
            )}
          </div>
        )}
      </div>

      {/* ── Footer ── buttons aligned to start (left LTR / right RTL) */}
      {!isLoading && (
        <div className='flex items-center justify-end gap-3 px-6 py-3.5 border-t border-sd-border'>
          {mode === 'enable' ? (
            <>
              <button
                onClick={handleConfirm}
                className='h-9 px-5 text-[13px] font-semibold text-sd-primary-foreground border-0 rounded-lg transition-colors cursor-pointer bg-sd-primary hover:opacity-90'
              >
                {String(t('confirm_and_enable_carrier'))}
              </button>
              <button
                onClick={onClose}
                className='h-9 px-5 text-[13px] font-medium text-sd-foreground bg-sd-card border border-sd-border rounded-lg hover:bg-sd-muted transition-colors cursor-pointer'
              >
                {String(t('cancel'))}
              </button>
            </>
          ) : (
            <button
              onClick={onClose}
              className='h-9 px-6 text-[13px] font-medium text-sd-foreground bg-sd-card border border-sd-border rounded-lg hover:bg-sd-muted transition-colors cursor-pointer'
            >
              {String(t('close'))}
            </button>
          )}
        </div>
      )}
    </Modal>
  );
}
